/**
 * What the chat composer says when it cannot do what the user asks
 * (mission stream contract phase 4, agion/Agion#2327): a composer that cannot
 * send or attach says why instead of pretending.
 */

import { unknownKind, type PasteRefusal } from '@agion/mission-stream'
import type { ExecutionMode, MissionRunReadiness } from '../../types/mission'
import { deliveryText } from './deliveryTexts'
import { isChatApiError } from '../../services/api-failure'

interface ComposerCopy {
  /** i18n key of the text */
  key: string
  /** English fallback when the key is missing */
  fallback: string
}

export const COMPOSER_TEXTS = {
  agentWorking: {
    key: 'chat:composer.agentWorking',
    fallback: 'The assistant is working. You can reply when it asks you a question.',
  },
  chooseAnOption: {
    key: 'chat:composer.chooseAnOption',
    fallback: 'Choose one of the answers above to reply.',
  },
  questionClosed: {
    key: 'chat:composer.questionClosed',
    fallback: 'This question is no longer open.',
  },
  attachUnavailable: {
    key: 'chat:composer.attachUnavailable',
    fallback: 'This assistant cannot receive files.',
  },
  attachFindingHost: {
    key: 'chat:composer.attachFindingHost',
    fallback: 'Finding the server that will take your files…',
  },
  attachGroupOffline: {
    key: 'chat:composer.attachGroupOffline',
    fallback: 'None of the servers this assistant runs on is online, so it cannot take files now. Try again when one is back.',
  },
  attachHostNotFound: {
    key: 'chat:composer.attachHostNotFound',
    fallback: 'The server that would take your files could not be found. Reload the page to try again.',
  },
  fileNotUploaded: {
    key: 'chat:composer.fileNotUploaded',
    fallback: 'A file did not upload. Retry it or remove it, then send.',
  },
  launchFailed: {
    key: 'chat:composer.launchFailed',
    fallback: 'The conversation could not start.',
  },
  tooLong: {
    key: 'chat:composer.tooLong',
    fallback: 'This message is too long to send. Please shorten it.',
  },
  pasteTooLong: {
    key: 'chat:composer.pasteTooLong',
    fallback: 'That paste would make the message too long to send, so it was not added.',
  },
  notReadyTool: {
    key: 'chat:composer.notReadyTool',
    fallback: 'This assistant cannot start yet because a tool it uses is not ready. Ask its owner to finish it.',
  },
  notReadySetup: {
    key: 'chat:composer.notReadySetup',
    fallback: 'This assistant cannot start yet because its setup is not finished. Ask its owner to finish it.',
  },
} as const satisfies Record<string, ComposerCopy>

export type ComposerText = keyof typeof COMPOSER_TEXTS

type Translate = (key: string, fallback: string) => string

/** The translated text. */
export function composerText(t: Translate, text: ComposerText): string {
  const { key, fallback } = COMPOSER_TEXTS[text]
  return t(key, fallback)
}

/** The question the composer can answer: a free-text one, not one with choices. */
interface SelectedQuestion {
  options?: string[]
}

/** The run as the composer sees it. */
interface ComposerRun {
  running: boolean
  ended: boolean
  /** The run takes a message while the agent works (acceptsMessagesWhileWorking). */
  acceptsMessages?: boolean
}

/**
 * Whether a run of this mode takes a message while the agent works, at the
 * agent's next turn (mission stream contract phase 6): adaptive and
 * autonomous runs do. A workflow run does not, and the host refuses it
 * (mode_does_not_accept). A mode the app does not know keeps phase 4's rule.
 */
export function acceptsMessagesWhileWorking(mode: ExecutionMode | undefined): boolean {
  switch (mode) {
    case 'adaptive':
    case 'autonomous':
      return true
    case 'workflow':
    case undefined:
      return false
    default:
      return unknownKind(mode, false)
  }
}

/**
 * Why the composer cannot send now, or undefined when it can. A running
 * mission takes a message as the answer to the selected free-text question;
 * a question with choices is answered with its choices. With no question
 * open, a run that accepts messages takes it for the agent's next turn
 * (phase 6), and any other run takes nothing (phase 4). A run that has not
 * started or has ended takes the text as a new run's input.
 */
export function sendBlockedReason(run: ComposerRun, selected: SelectedQuestion | null): ComposerText | undefined {
  if (!run.running || run.ended) return undefined
  if (!selected) return run.acceptsMessages ? undefined : 'agentWorking'
  return selected.options?.length ? 'chooseAnOption' : undefined
}

/** The staged files, as far as sending is concerned. */
export interface StagedFiles {
  /** A send was pressed while files still upload; it waits for them. */
  held: boolean
  /** A file did not upload; it stays staged until it is retried or removed. */
  failed: boolean
}

/**
 * What the composer says instead of sending, or undefined when it can send.
 * A held send says it waits for its files (the waiting delivery state). Then
 * the run's reason comes first, because retrying a file does not help while
 * nothing can take the message; then a file that did not upload, which would
 * otherwise be left behind.
 */
export function sendDisabledText(
  t: Translate,
  run: ComposerRun,
  selected: SelectedQuestion | null,
  files: StagedFiles,
  draft: { tooLong: boolean } = { tooLong: false },
): string | undefined {
  if (files.held) return deliveryText(t, 'waiting')
  const blocked = sendBlockedReason(run, selected)
  if (blocked) return composerText(t, blocked)
  if (files.failed) return composerText(t, 'fileNotUploaded')
  // Text over MESSAGE_TEXT_MAX_BYTES (@agion/mission-stream) is never sent.
  return draft.tooLong ? composerText(t, 'tooLong') : undefined
}

/** What chat knows about where a conversation's files would go. */
export interface AttachTarget {
  /** The Edge host that takes this conversation's files, when known. */
  hostId?: string
  /** The mission runs on a host group (host_group_id) rather than one host. */
  hostGroupBound: boolean
  /** The placement read for a group-bound mission with no known host. */
  placement: { pending: boolean; failure?: unknown }
}

/**
 * Why attach is off, or undefined when files can be attached. Only a mission
 * with no Edge host at all cannot receive files. A group-bound mission runs
 * on the customer's servers: it waits for the platform to name the member a
 * run goes to, and when none can take the files it says why
 * (agion/Agion#2485).
 */
export function attachBlockedReason(target: AttachTarget): ComposerText | undefined {
  if (target.hostId) return undefined
  if (!target.hostGroupBound) return 'attachUnavailable'
  if (target.placement.pending) return 'attachFindingHost'
  const failure = target.placement.failure
  // orchestration-service: 503 placement_host_unavailable, or a pinned
  // follow-up whose host is offline (edge_host_offline).
  if (isChatApiError(failure) && (failure.code === 'placement_host_unavailable' || failure.code === 'edge_host_offline')) {
    return 'attachGroupOffline'
  }
  return 'attachHostNotFound'
}

/**
 * Why a paste was not taken, as the composer says it. A paste of files where
 * none can be attached says why attach is off.
 */
export function pasteRefusalText(t: Translate, reason: PasteRefusal, attachBlocked: ComposerText = 'attachUnavailable'): string {
  switch (reason) {
    case 'too_long':
      return composerText(t, 'pasteTooLong')
    case 'no_attachments':
      return composerText(t, attachBlocked)
    default:
      return unknownKind(reason, composerText(t, 'pasteTooLong'))
  }
}

/**
 * Why a new run cannot start, from the server's run readiness (persona
 * F-CHAT-08, agion/Agion#2557), or undefined when the server said it can or
 * has not answered. Only the server's reason code is translated here; a code
 * this chat does not know reads as unfinished setup.
 */
export function runNotReadyText(t: Translate, readiness: MissionRunReadiness | undefined): string | undefined {
  if (readiness?.ready !== false) return undefined
  return composerText(t, readiness.reason === 'tool_not_ready' ? 'notReadyTool' : 'notReadySetup')
}
